import { useState, useMemo } from "react";
import { Plus, Receipt, Loader2, Pencil, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { useExpenses } from "../hooks/useExpenses";
import { useDrivers } from "../hooks/useDrivers";
import ExpenseFormModal from "./ExpenseFormModal";

const CATEGORY_LABELS = {
  toll: "Toll", repair: "Repair", insurance_premium: "Insurance Premium", salary: "Driver Salary", other: "Other",
};
const CATEGORY_COLORS = {
  toll: { color: "#2563EB", bg: "#EFF6FF" },
  repair: { color: "#D97706", bg: "#FFFBEB" },
  insurance_premium: { color: "#4F46E5", bg: "#EEF2FF" },
  salary: { color: "#16A34A", bg: "#F0FDF4" },
  other: { color: "#6B7280", bg: "#F3F4F6" },
};

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function ExpensesPanel({ vehicles }) {
  const { expenses, loading, addExpense, updateExpense, removeExpense } = useExpenses();
  const { drivers } = useDrivers();
  const [showForm,      setShowForm]      = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  const monthTotal = useMemo(() => {
    const now = new Date();
    return expenses
      .filter(e => {
        const d = new Date(e.expenseDate);
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      })
      .reduce((sum, e) => sum + e.amount, 0);
  }, [expenses]);

  function openAdd()  { setEditingExpense(null); setShowForm(true); }
  function openEdit(e) { setEditingExpense(e);   setShowForm(true); }

  function vehicleLabel(vehicleId) {
    if (!vehicleId) return null;
    const v = vehicles.find(v => v.id === vehicleId);
    return v ? (v.nickname || v.plateNumber) : null;
  }

  function driverLabel(driverId) {
    if (!driverId) return null;
    const d = drivers.find(d => d.id === driverId);
    return d ? d.name : null;
  }

  async function handleSubmit(payload) {
    if (editingExpense) await updateExpense(editingExpense.id, payload);
    else                 await addExpense(payload);
  }

  async function handleDelete(expense) {
    if (!window.confirm(`Remove this ${CATEGORY_LABELS[expense.category]} expense?`)) return;
    try {
      await removeExpense(expense.id);
      toast.success("Expense removed");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to remove expense");
    }
  }

  return (
    <main style={{ flex: 1, overflow: "auto", background: "#F9FAFB" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "28px 24px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#111827" }}>Expenses</div>
            <div style={{ fontSize: 13, color: "#6B7280", marginTop: 2 }}>
              Tolls, repairs, insurance premiums, salaries, and other running costs
              {" — "}<strong style={{ color: "#111827" }}>₹{monthTotal.toLocaleString("en-IN")}</strong> this month
            </div>
          </div>
          <button
            onClick={openAdd}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "#4F46E5", border: "none", borderRadius: 8,
              padding: "0 16px", height: 38, cursor: "pointer",
              fontSize: 13.5, fontWeight: 700, color: "#fff",
            }}
          >
            <Plus size={15} /> Add Expense
          </button>
        </div>

        {loading && (
          <div style={{ padding: 60, textAlign: "center", color: "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> Loading expenses…
          </div>
        )}

        {!loading && expenses.length === 0 && (
          <div style={{
            background: "#fff", border: "1px dashed #D1D5DB", borderRadius: 14,
            padding: "48px 24px", textAlign: "center",
          }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 6 }}>No expenses yet</div>
            <div style={{ fontSize: 13, color: "#9CA3AF", marginBottom: 18 }}>Log tolls, repairs, insurance, and salaries to track true running cost.</div>
            <button
              onClick={openAdd}
              style={{ background: "#4F46E5", border: "none", borderRadius: 8, padding: "0 18px", height: 38, color: "#fff", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}
            >
              <Plus size={14} style={{ marginRight: 6, verticalAlign: -2 }} /> Add Expense
            </button>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {expenses.map(expense => {
            const catStyle = CATEGORY_COLORS[expense.category];
            const vLabel = vehicleLabel(expense.vehicleId);
            const dLabel = driverLabel(expense.driverId);
            return (
              <div key={expense.id} style={{
                background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12,
                padding: "16px 18px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                  background: catStyle.bg, color: catStyle.color,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Receipt size={18} />
                </div>

                <div style={{ flex: "1 1 260px", minWidth: 200 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: "#111827" }}>
                    ₹{expense.amount.toLocaleString("en-IN")} — {CATEGORY_LABELS[expense.category]}
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 4, flexWrap: "wrap", fontSize: 12, color: "#6B7280" }}>
                    <span>{fmtDate(expense.expenseDate)}</span>
                    {vLabel && <span>{vLabel}</span>}
                    {dLabel && <span>{dLabel}</span>}
                    {expense.notes && <span>{expense.notes}</span>}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={() => openEdit(expense)}
                    title="Edit expense"
                    style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 7, width: 32, height: 32, cursor: "pointer", color: "#6B7280", display: "flex", alignItems: "center", justifyContent: "center" }}
                  ><Pencil size={13} /></button>
                  <button
                    onClick={() => handleDelete(expense)}
                    title="Remove expense"
                    style={{ background: "none", border: "1px solid #FECACA", borderRadius: 7, width: 32, height: 32, cursor: "pointer", color: "#DC2626", display: "flex", alignItems: "center", justifyContent: "center" }}
                  ><Trash2 size={13} /></button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showForm && (
        <ExpenseFormModal
          expense={editingExpense}
          vehicles={vehicles}
          drivers={drivers}
          onClose={() => setShowForm(false)}
          onSubmit={handleSubmit}
        />
      )}
    </main>
  );
}
